import { useQueryClient } from '@tanstack/react-query';
import { TextileAppState } from 'api/textile_deals/fetchTextileAppState';
import TopActionBlock from 'blocks/TopActionBlock';
import HelpSubject from 'components/HelpButton/HelpSubject';
import GenericErrorComponent from 'components/InfoBoxComponent/GenericErrorComponent';
import LoadingIndicatorComponent from 'components/LoadingIndicatorComponent';
import PageHeader from 'components/PageHeader/PageHeader';
import PageStackBlock from 'components/PageStackBlock/PageStackBlock';
import { HasAccessContext } from 'contexts/HasAccessContext';
import BackClickLink from 'domain/Links/BackClickLink';
import InputMessage from 'elements/InputMessage';
import { useOrderOverviewQuery } from 'queries/textile_deals/useOrderOverviewQueries';
import React, { useContext, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import TextileOrderOverviewList from 'screens/order/screens/components/TextileOrderOverviewList';
import { HistoryProps } from 'utils/history';
import { textileOrderIdFromMatch } from 'utils/history/textile-order-id-from-match';

const PreorderOverviewScreen: React.FC<HistoryProps> = (props) => {
  const textileOrderId = textileOrderIdFromMatch(props.match);
  const queryClient = useQueryClient();
  const accessContext = useContext(HasAccessContext);
  const textileAppState = queryClient.getQueryData<TextileAppState>([
    'textile_app_state',
    textileOrderId
  ]);

  const orderOverview = useOrderOverviewQuery(textileOrderId);

  if (!accessContext) {
    throw Error('Component must be used within HasAccessContextProvider');
  }

  useEffect(() => {
    accessContext.checkSectionAccess('preorder');
  }, [accessContext, orderOverview.data]);

  const renderPage = (content: React.ReactNode) => {
    return (
      <>
        <TopActionBlock>
          <BackClickLink onClick={() => props.history.goBack()} />
        </TopActionBlock>

        <PageStackBlock>{content}</PageStackBlock>

        <HelpSubject subject="order" />
      </>
    );
  };

  if (orderOverview.isError) {
    return renderPage(
      <GenericErrorComponent onRetryClick={() => orderOverview.refetch()} />
    );
  }

  if (!textileAppState || orderOverview.isLoading || !orderOverview.data) {
    return renderPage(<LoadingIndicatorComponent />);
  }

  const renderOverview = () => {
    const order = orderOverview.data.orders;
    const preordersAmount = orderOverview.data.preorders?.length || 0;

    return renderPage(
      <>
        <PageHeader
          headline={<FormattedMessage id="textile order overview" />}
          text={<FormattedMessage id="textile order overview total" />}
        />

        {!order || order.length === 0 ? (
          <InputMessage info={true}>
            <FormattedMessage id="no preorder submitted" />
          </InputMessage>
        ) : (
          <TextileOrderOverviewList
            preordersAmount={preordersAmount}
            order={order}
          />
        )}
      </>
    );
  };

  return renderOverview();
};

export default PreorderOverviewScreen;
